// issuesAndTasksApi.js - API utilities for Issues and Tasks Dashboard

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for issues and tasks API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body : errorData.body?.error || 'Unknown error'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_issues_tasks',
        `I&T Dashboard API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[issuesAndTasksApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_issues_tasks',
      `I&T Dashboard API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[issuesAndTasksApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Issues and Tasks Dashboard.
 */
export const issuesAndTasksApi = {
  /**
   * Fetches all sites.
   * GET /dashboard/site/getall
   * @returns {Promise<Array>} - Array of site objects.
   * @throws {Error} - Throws an error if the API request fails.
   */
  fetchAllSites: async () => {
    try {
      const response = await makeApiRequest('/dashboard/site/getall');
      const data = await response.json();
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_issues_tasks',
        `Successfully fetched all sites`,
        {
          endpoint: '/dashboard/site/getall',
          method: 'GET',
        }
      ).catch(logError => {
        console.warn('[issuesAndTasksApi] Failed to log success event:', logError);
      });
      
      return data;
    } catch (error) {
      console.error('Error fetching sites:', error);
      throw error;
    }
  },

  /**
   * Fetches unresolved issues data per site.
   * GET /dashboard/data/unresolved-issues
   * @returns {Promise<Map<string, number>>} - Map of site ID to unresolved issue count.
   * @throws {Error} - Throws an error if the API request fails.
   */
  getUnresolvedIssuesData: async () => {
    const response = await makeApiRequest('/dashboard/data/unresolved-issues');
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_issues_tasks',
      `Successfully fetched unresolved issues data`,
      {
        endpoint: '/dashboard/data/unresolved-issues',
        method: 'GET',
      }
    ).catch(logError => {
      console.warn('[issuesAndTasksApi] Failed to log success event:', logError);
    });
    
    // Convert the response to a Map-like structure
    return new Map(Object.entries(data));
  },

  /**
   * Fetches task and issue resolution data for a specific week.
   * GET /dashboard/data/task-and-issue-resolution?date={date}
   * @param {string} date - The date in YYYY-MM-DD format (starting date of the week).
   * @returns {Promise<Object>} - Task and issue resolution data.
   * @throws {Error} - Throws an error if the API request fails or date format is invalid.
   */
  getTaskAndIssueResolutionData: async (date) => {
    if (!date) {
      throw new Error('Date parameter is required');
    }

    const endpoint = `/dashboard/data/task-and-issue-resolution?date=${date}`;
    const response = await makeApiRequest(endpoint);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_issues_tasks',
      `Successfully fetched task and issue resolution data`,
      {
        endpoint: '/dashboard/data/task-and-issue-resolution',
        method: 'GET',
        date,
      }
    ).catch(logError => {
      console.warn('[issuesAndTasksApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Fetches weekly average delays per site.
   * GET /dashboard/data/avg-delays?date={date}
   * @param {string} date - The date in YYYY-MM-DD format (starting date of the week).
   * @returns {Promise<Object>} - Average delay data.
   * @throws {Error} - Throws an error if the API request fails or date format is invalid.
   */
  getWeeklyAverageDelays: async (date) => {
    if (!date) {
      throw new Error('Date parameter is required');
    }

    const endpoint = `/dashboard/data/avg-delays?date=${date}`;
    const response = await makeApiRequest(endpoint);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_issues_tasks',
      `Successfully fetched weekly average delays`,
      {
        endpoint: '/dashboard/data/avg-delays',
        method: 'GET',
        date,
      }
    ).catch(logError => {
      console.warn('[issuesAndTasksApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Fetches weekly issue data (raised and solved).
   * GET /dashboard/data/weekly-issue-data?date={date}
   * @param {string} date - The date in YYYY-MM-DD format (starting date of the week).
   * @returns {Promise<Object>} - Weekly issue data.
   * @throws {Error} - Throws an error if the API request fails or date format is invalid.
   */
  getWeeklyIssueData: async (date) => {
    if (!date) {
      throw new Error('Date parameter is required');
    }

    const endpoint = `/dashboard/data/weekly-issue-data?date=${date}`;
    const response = await makeApiRequest(endpoint);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_issues_tasks',
      `Successfully fetched weekly issue data`,
      {
        endpoint: '/dashboard/data/weekly-issue-data',
        method: 'GET',
        date,
      }
    ).catch(logError => {
      console.warn('[issuesAndTasksApi] Failed to log success event:', logError);
    });
    
    return data;
  },
};
